import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import ModuleHeader from "../../components/moduleHeader";
import Footer from "../../components/footer";
import { getProfile } from "../../services/api";
import { getTokenStats } from "../../services/tokenService";
import Notiflix from "notiflix";

function TokenBalance() {
  const navigate = useNavigate();
  const { id: uuid } = useParams();
  const [loading, setLoading] = useState(true);
  const [userData, setUserData] = useState(null);
  const [tokenStats, setTokenStats] = useState(null);
  const [isHeaderExpanded, setIsHeaderExpanded] = useState(false);

  useEffect(() => {
    Notiflix.Notify.init({
      position: "top-right",
      timeout: 3000,
      clickToClose: true,
      pauseOnHover: true,
    });
  }, []);

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        const [profileResponse, statsResponse] = await Promise.all([
          getProfile(),
          getTokenStats()
        ]);
        
        setUserData(profileResponse);
        
        if (statsResponse.success && statsResponse.stats) {
          setTokenStats(statsResponse.stats);
        }
      } catch (error) {
        console.error("Veri alınamadı:", error);
        Notiflix.Notify.failure("Token bilgileri yüklenirken bir hata oluştu!");
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  // Loading durumunda loading ekranı göster
  if (loading || !userData) {
    return (
      <div className="w-full h-screen min-h-screen flex items-center justify-center bg-[#fff]">
        <div className="flex flex-col items-center justify-center">
          <div className="relative mb-8">
            <div className="absolute inset-0 flex items-center justify-center">
              <div className="animate-spin rounded-full h-24 w-24 border-4 border-transparent border-t-[#3DA7B5] border-r-[#3DA7B5] opacity-75"></div>
            </div>
            <div className="absolute inset-0 flex items-center justify-center">
              <div
                className="animate-spin rounded-full h-16 w-16 border-[3px] border-transparent border-b-[#2a8a96] border-l-[#2a8a96] opacity-60"
                style={{
                  animationDirection: "reverse",
                  animationDuration: "1.5s",
                }}
              ></div>
            </div>
            <div className="absolute inset-0 flex items-center justify-center">
              <div className="h-12 w-12 rounded-full bg-[#3DA7B5] animate-pulse shadow-2xl"></div>
            </div>
          </div>
          <p className="text-xl font-semibold text-gray-800 animate-pulse">
            Yükleniyor...
          </p>
        </div>
      </div>
    );
  }

  const fullName = userData?.user ? `${userData.user.first_name} ${userData.user.last_name}` : "";

  return (
    <div className="w-full h-screen min-h-screen flex items-stretch justify-stretch sm:items-center sm:justify-center bg-[#e8e9ed] relative">
      <div className="flex flex-col items-center w-full min-h-full bg-white shadow-xl border border-gray-200 rounded-none sm:max-w-[390px] sm:h-[700px] sm:rounded-3xl relative overflow-y-auto">
        {/* ModuleHeader */}
        <div className="relative z-30 w-full mb-2">
          <ModuleHeader
            userName={fullName}
            subtitle="Token Modülüne Hoş Geldiniz"
            title="JT Bakiyem"
            description="Jodiac Token bakiyenizi ve işlem geçmişinizi görüntüleyin."
            backgroundImage="/assets/settings.jpg"
            onExpandedChange={setIsHeaderExpanded}
            showToggle={true}
            autoHeight={true}
            fontFamily="font-poppins"
          />
        </div>

        {/* İçerik */}
        <div className="w-full px-6 pb-20 flex-1">
          {tokenStats ? (
            <div className="space-y-6 pt-4">
              {/* Güncel Bakiye - Büyük Kart */}
              <div className="bg-[#3DA7B5] rounded-2xl p-6 shadow-lg">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-white/90 text-sm font-medium font-poppins">Güncel Bakiye</span>
                  <svg className="w-8 h-8 text-white/80" fill="currentColor" viewBox="0 0 20 20">
                    <path d="M8.433 7.418c.155-.103.346-.196.567-.267v1.698a2.305 2.305 0 01-.567-.267C8.07 8.34 8 8.114 8 8c0-.114.07-.34.433-.582zM11 12.849v-1.698c.22.071.412.164.567.267.364.243.433.468.433.582 0 .114-.07.34-.433.582a2.305 2.305 0 01-.567.267z" />
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm1-13a1 1 0 10-2 0v.092a4.535 4.535 0 00-1.676.662C6.602 6.234 6 7.009 6 8c0 .99.602 1.765 1.324 2.246.48.32 1.054.545 1.676.662v1.941c-.391-.127-.68-.317-.843-.504a1 1 0 10-1.51 1.31c.562.649 1.413 1.076 2.353 1.253V15a1 1 0 102 0v-.092a4.535 4.535 0 001.676-.662C13.398 13.766 14 12.991 14 12c0-.99-.602-1.765-1.324-2.246A4.535 4.535 0 0011 9.092V7.151c.391.127.68.317.843.504a1 1 0 101.511-1.31c-.563-.649-1.413-1.076-2.354-1.253V5z" clipRule="evenodd" />
                  </svg>
                </div>
                <div className="flex items-baseline gap-2">
                  <span className="text-4xl font-bold text-white font-poppins">{tokenStats.current_balance}</span>
                  <span className="text-white/80 text-lg font-semibold font-poppins">JT</span>
                </div>
                <p className="text-white/70 text-xs mt-2 font-poppins">Jodiac Token</p>
              </div>

              {/* JT Satın Al Butonu */}
              <a
                href="https://www.jodi.ac"
                target="_blank"
                rel="noopener noreferrer"
                className="block w-full bg-white border-2 border-[#3DA7B5] text-[#3DA7B5] font-bold py-3.5 px-6 rounded-xl shadow-md hover:bg-[#3DA7B5] hover:text-white transition-all duration-300 transform hover:scale-[1.02] active:scale-[0.98] text-center font-poppins"
              >
                <span className="flex items-center justify-center gap-2">
                  <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                    <path d="M3 1a1 1 0 000 2h1.22l.305 1.222a.997.997 0 00.01.042l1.358 5.43-.893.892C3.74 11.846 4.632 14 6.414 14H15a1 1 0 000-2H6.414l1-1H14a1 1 0 00.894-.553l3-6A1 1 0 0017 3H6.28l-.31-1.243A1 1 0 005 1H3zM16 16.5a1.5 1.5 0 11-3 0 1.5 1.5 0 013 0zM6.5 18a1.5 1.5 0 100-3 1.5 1.5 0 000 3z" />
                  </svg>
                  JT Satın Al
                </span>
              </a>

              {/* İstatistikler */}
              <div className="space-y-3">
                <h3 className="text-gray-700 font-semibold text-sm mb-3 font-poppins">İstatistikler</h3>
                
                <div className="bg-gray-50 rounded-xl p-4 border border-gray-200">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 bg-green-100 rounded-full flex items-center justify-center">
                        <svg className="w-5 h-5 text-green-600" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                        </svg>
                      </div>
                      <div>
                        <p className="text-xs text-gray-500 font-poppins">Toplam Kazanılan</p>
                        <p className="text-lg font-bold text-gray-800 font-poppins">{tokenStats.lifetime_earned} JT</p>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="bg-gray-50 rounded-xl p-4 border border-gray-200">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 bg-red-100 rounded-full flex items-center justify-center">
                        <svg className="w-5 h-5 text-red-600" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                        </svg>
                      </div>
                      <div>
                        <p className="text-xs text-gray-500 font-poppins">Toplam Harcanan</p>
                        <p className="text-lg font-bold text-gray-800 font-poppins">{tokenStats.lifetime_spent} JT</p>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-3">
                  <div className="bg-gray-50 rounded-xl p-4 border border-gray-200">
                    <div className="text-center">
                      <p className="text-xs text-gray-500 mb-1 font-poppins">İşlem Sayısı</p>
                      <p className="text-2xl font-bold text-[#3DA7B5] font-poppins">{tokenStats.total_transactions}</p>
                    </div>
                  </div>
                  
                  <div className="bg-gray-50 rounded-xl p-4 border border-gray-200">
                    <div className="text-center">
                      <p className="text-xs text-gray-500 mb-1 font-poppins">Harcama Sayısı</p>
                      <p className="text-2xl font-bold text-[#3DA7B5] font-poppins">{tokenStats.total_spending}</p>
                    </div>
                  </div>
                </div>

                <div className="bg-gray-50 rounded-xl p-4 border border-gray-200">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center">
                        <svg className="w-5 h-5 text-blue-600" fill="currentColor" viewBox="0 0 20 20">
                          <path d="M3 1a1 1 0 000 2h1.22l.305 1.222a.997.997 0 00.01.042l1.358 5.43-.893.892C3.74 11.846 4.632 14 6.414 14H15a1 1 0 000-2H6.414l1-1H14a1 1 0 00.894-.553l3-6A1 1 0 0017 3H6.28l-.31-1.243A1 1 0 005 1H3zM16 16.5a1.5 1.5 0 11-3 0 1.5 1.5 0 013 0zM6.5 18a1.5 1.5 0 100-3 1.5 1.5 0 000 3z" />
                        </svg>
                      </div>
                      <div>
                        <p className="text-xs text-gray-500 font-poppins">Satın Alma</p>
                        <p className="text-lg font-bold text-gray-800 font-poppins">{tokenStats.total_purchases}</p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          ) : (
            <div className="flex flex-col items-center justify-center py-12">
              <svg className="w-16 h-16 text-gray-400 mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              <p className="text-gray-500 text-center">Token bilgileri yüklenemedi.</p>
            </div>
          )}
        </div>

        <Footer uuid={uuid} />
      </div>
    </div>
  );
}

export default TokenBalance;

